import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

/**
 * Promise-based confirm dialog. Lets callers do:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete this entry?',
 *     message: "You'll have a few seconds to undo.",
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *   await actuallyDelete();
 *
 * Replaces the manual [open, setOpen] state plumbing every page does for
 * destructive actions. One <ConfirmProvider> at app root, then any component
 * can call useConfirm() without rendering its own dialog.
 *
 * Mount once near the top of the app tree (App.js, inside ErrorBoundary).
 */

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx.confirm;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, options: null });
  const [loading, setLoading] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const handleResolve = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setState({ open: false, options: state.options });
    setLoading(false);
  }, [state.options]);

  const handleConfirm = useCallback(async () => {
    // If the caller passed an async onConfirm, run it before resolving.
    const onConfirm = state.options?.onConfirm;
    if (typeof onConfirm === 'function') {
      try {
        setLoading(true);
        await onConfirm();
        handleResolve(true);
      } catch (err) {
        setLoading(false);
        // Don't auto-close on error — let the caller surface it via toast.
        handleResolve(false);
      }
    } else {
      handleResolve(true);
    }
  }, [state.options, handleResolve]);

  const handleCancel = useCallback(() => {
    handleResolve(false);
  }, [handleResolve]);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={state.open}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        loading={loading}
        title={state.options?.title ?? 'Are you sure?'}
        message={state.options?.message}
        confirmLabel={state.options?.confirmLabel ?? 'Confirm'}
        cancelLabel={state.options?.cancelLabel ?? 'Cancel'}
        destructive={!!state.options?.destructive}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
